import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  mergeLlmChatSessionIntoList,
  patchLlmChatSessionInListCache,
  useCreateLlmChatSession,
  useLlmChatProviderInfo,
  useLlmChatSessionsList,
} from '@/shared/hooks/api/llmChat'
import { useListProducts } from '@/shared/hooks/api/product'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { LegislationViewerModal } from './components/LegislationViewerModal'
import { LlmChatComposer } from './components/LlmChatComposer'
import { LlmChatNewChatWizard } from './components/LlmChatNewChatWizard'
import { LlmChatSessionThread } from './components/LlmChatSessionThread'
import { LlmChatSidebar } from './components/LlmChatSidebar'
import type { LegislationCitationTarget } from './components/llmChatToolOutput'
import { isAnnexCitationLabel } from './components/llmChatToolOutput'
import { useLlmChatDraftState } from './hooks/useLlmChatDraftState'
import { useLlmChatSessionLoader } from './hooks/useLlmChatSessionLoader'
import { llmChatSessionPath } from './llmChatPaths'
import {
  selectSession,
  useLlmChatStore,
} from './store/llmChatStore'

export function LlmChatPage() {
  const { t } = useTranslation('llmChat')
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { sessionId: sessionIdParam } = useParams<{ sessionId?: string }>()
  const sessionId = sessionIdParam ?? undefined
  const [searchParams] = useSearchParams()
  const isNewDraft = searchParams.get('new') === '1'

  useEffect(() => {
    const legacySessionId = searchParams.get('session')
    if (legacySessionId && !sessionId) {
      navigate(llmChatSessionPath(legacySessionId), { replace: true })
    }
  }, [navigate, searchParams, sessionId])
  const listQuery = useLlmChatSessionsList()
  const productsQuery = useListProducts()
  const providerInfoQuery = useLlmChatProviderInfo()
  const chatModelOptions = providerInfoQuery.data?.models.chatModelOptions ?? []
  const defaultChatModelId = providerInfoQuery.data?.models.stateOfTheArtChat
  const landingModel = useLlmChatStore((state) => state.landingModel)
  const setLandingModel = useLlmChatStore((state) => state.setLandingModel)
  const landingDisplayModel =
    landingModel ??
    defaultChatModelId ??
    (chatModelOptions.length > 0 ? chatModelOptions[0] : undefined)
  const sessionLoader = useLlmChatSessionLoader(sessionId)
  const activeSession = useLlmChatStore((state) =>
    selectSession(state, sessionId)
  )
  const [citationTarget, setCitationTarget] =
    useState<LegislationCitationTarget | null>(null)
  const [isLegislationViewerOpen, setIsLegislationViewerOpen] = useState(false)
  const createSession = useCreateLlmChatSession()

  const sessions = useMemo(() => {
    const list = listQuery.data?.sessions ?? []
    if (
      sessionId === undefined ||
      activeSession === undefined ||
      activeSession === null
    ) {
      return list
    }
    return mergeLlmChatSessionIntoList(list, activeSession)
  }, [activeSession, listQuery.data?.sessions, sessionId])

  useEffect(() => {
    if (activeSession === undefined || activeSession === null) {
      return
    }
    patchLlmChatSessionInListCache(queryClient, activeSession)
  }, [activeSession, queryClient])

  const products = useMemo(() => productsQuery.data ?? [], [productsQuery.data])
  const {
    draftMode,
    selectedDraftProduct,
    setDraftParams,
    productDraftCanCompose,
    canComposeDraft,
  } = useLlmChatDraftState({
    searchParams,
    navigate,
    sessionId,
    isNewDraft,
    products,
    productsLoaded: productsQuery.isSuccess,
  })

  useEffect(() => {
    if (sessionId) {
      return
    }

    if (isNewDraft) {
      return
    }

    if (listQuery.isLoading) {
      return
    }

    if (sessions.length > 0) {
      navigate(llmChatSessionPath(sessions[0].id), { replace: true })
    }
  }, [sessionId, isNewDraft, listQuery.isLoading, sessions, navigate])

  const handleLandingSend = useCallback(
    async (text: string) => {
      if (!text || createSession.isPending || !canComposeDraft) {
        return
      }

      const response = await createSession.mutateAsync({
        systemPromptKey: 'lex_chat_default',
        ...(productDraftCanCompose
          ? { productId: selectedDraftProduct!.id }
          : {}),
      })
      const store = useLlmChatStore.getState()
      store.initializeSessionFromServer(response.session.id, {
        session: response.session,
        messages: [],
      })
      store.setBootstrapMessage(response.session.id, text)
      const model = store.getLandingModel()
      if (model !== undefined && model !== '') {
        store.setBootstrapModel(response.session.id, model)
      }
      navigate(llmChatSessionPath(response.session.id), { replace: true })
    },
    [
      canComposeDraft,
      createSession,
      navigate,
      productDraftCanCompose,
      selectedDraftProduct,
    ]
  )

  const handleNewChat = () => {
    setDraftParams({ mode: null, productId: null })
  }

  const handleSelectSession = (nextSessionId: string) => {
    navigate(llmChatSessionPath(nextSessionId), { replace: true })
  }

  const showLandingComposer =
    !sessionId &&
    (isNewDraft || (!listQuery.isLoading && sessions.length === 0))

  const showBootstrapLoading =
    !sessionId && !isNewDraft && (listQuery.isLoading || sessions.length > 0)

  const showSessionLoading = Boolean(sessionId) && sessionLoader.isLoading

  return (
    <>
      <Header fixed>
        <Search />
        <div className='flex min-w-0 flex-1 items-center'>
          <h1 className='truncate text-lg font-semibold'>{t('title')}</h1>
        </div>
        <div className='ms-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main fixed fluid className='flex min-h-0 flex-1 flex-col'>
        <div className='flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-border/60 bg-muted/5 md:flex-row dark:bg-muted/10'>
          <LlmChatSidebar
            sessions={sessions}
            selectedSessionId={sessionId}
            isLoading={listQuery.isLoading}
            onSelectSession={handleSelectSession}
            onNewChat={handleNewChat}
          />

          <div className='flex min-h-0 min-w-0 flex-1 flex-col bg-background'>
            {showLandingComposer ? (
              <>
                <ScrollArea className='min-h-0 flex-1'>
                  <LlmChatNewChatWizard
                    draftMode={draftMode}
                    productsLoading={productsQuery.isLoading}
                    products={products}
                    selectedDraftProduct={selectedDraftProduct}
                    onChooseGeneral={() => {
                      setDraftParams({
                        mode: 'general',
                        productId: null,
                      })
                    }}
                    onChooseProduct={() => {
                      setDraftParams({
                        mode: 'product',
                        productId: null,
                      })
                    }}
                    onBack={() => {
                      setDraftParams({
                        mode: null,
                        productId: null,
                      })
                    }}
                    onOpenCatalog={() => {
                      navigate('/product-catalog')
                    }}
                    onContinueProduct={(product) => {
                      setDraftParams({
                        mode: 'product',
                        productId: product.id,
                      })
                    }}
                  />
                </ScrollArea>
                {canComposeDraft ? (
                  <LlmChatComposer
                    key='landing'
                    onSend={handleLandingSend}
                    isSending={createSession.isPending}
                    modelOptions={chatModelOptions}
                    selectedModel={landingDisplayModel}
                    onModelChange={(model) => {
                      setLandingModel(model)
                    }}
                  />
                ) : null}
              </>
            ) : showBootstrapLoading ? (
              <div className='flex flex-1 items-center justify-center'>
                <Loader2 className='size-8 animate-spin text-muted-foreground' />
              </div>
            ) : showSessionLoading ? (
              <div className='flex flex-1 items-center justify-center'>
                <Loader2 className='size-8 animate-spin text-muted-foreground' />
              </div>
            ) : sessionLoader.isError ? (
              <div className='flex flex-1 flex-col items-center justify-center gap-2 p-4 text-center text-sm text-muted-foreground'>
                <p>{t('sessionLoadError')}</p>
                <Button
                  type='button'
                  variant='secondary'
                  onClick={handleNewChat}
                >
                  {t('startNewChat')}
                </Button>
              </div>
            ) : sessionId ? (
              <LlmChatSessionThread
                key={sessionId}
                sessionId={sessionId}
                onOpenCitation={(target) => {
                  if (
                    target.articleLabel &&
                    isAnnexCitationLabel(target.articleLabel)
                  ) {
                    return
                  }
                  setCitationTarget(target)
                  setIsLegislationViewerOpen(true)
                }}
              />
            ) : null}
          </div>
        </div>
      </Main>
      <LegislationViewerModal
        open={isLegislationViewerOpen}
        target={citationTarget}
        onOpenChange={(nextOpen) => {
          setIsLegislationViewerOpen(nextOpen)
          if (!nextOpen) {
            setCitationTarget(null)
          }
        }}
      />
    </>
  )
}
